import { CheckCircle2, Circle, MinusCircle } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { ORG_WORKSPACE_TOUR_HREF } from 'components/MapPage/orgWorkspaceTour/orgWorkspaceTourSteps';
import { useAorText } from 'utils/aorTerminology';
import {
  ONBOARDING_DATA_STEPS,
  stepStatus,
  type OnboardingCounts,
  type OnboardingDataStep,
} from '../onboardingModel';

const EVENT_SETTINGS_HREF = '/hub?openTab=Events&eventSettings=1';

export function FinishStep({
  counts,
  skipped,
  completedAt,
  canEdit,
  completing,
  onComplete,
  onRestart,
  onNavigate,
}: {
  counts: OnboardingCounts;
  skipped: readonly OnboardingDataStep[];
  completedAt: string | null;
  canEdit: boolean;
  completing: boolean;
  onComplete: () => void;
  onRestart: () => void;
  /** Called before following a link out of the sheet. */
  onNavigate: () => void;
}) {
  const aorText = useAorText();
  const labels: Record<OnboardingDataStep, string> = {
    aors: aorText(`${counts.aors} AOR${counts.aors === 1 ? '' : 's'}`),
    assets: `${counts.assets} asset${counts.assets === 1 ? '' : 's'}`,
    rules: `${counts.rules} automation rule${counts.rules === 1 ? '' : 's'}`,
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div>
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">
          {completedAt ? 'Organization setup is complete' : 'Review and finish'}
        </h3>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          You can come back to any step from the menu at any time.
        </p>
      </div>
      <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
        {ONBOARDING_DATA_STEPS.map((step) => {
          const status = stepStatus(step, counts, skipped);
          const Icon =
            status === 'done'
              ? CheckCircle2
              : status === 'skipped'
                ? MinusCircle
                : Circle;
          return (
            <li
              key={step}
              className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
            >
              <Icon
                className={
                  status === 'done'
                    ? 'tw:size-4 tw:text-emerald-500'
                    : 'tw:size-4 tw:text-muted-foreground'
                }
              />
              {labels[step]}
              {status === 'skipped' ? (
                <span className="tw:text-muted-foreground">(skipped)</span>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="tw:flex tw:flex-wrap tw:gap-2">
        <Button
          variant="outline"
          nativeButton={false}
          render={<Link to={ORG_WORKSPACE_TOUR_HREF} onClick={onNavigate} />}
        >
          Take the workspace tour
        </Button>
        <Button
          variant="outline"
          nativeButton={false}
          render={<Link to={EVENT_SETTINGS_HREF} onClick={onNavigate} />}
        >
          Open automation rules
        </Button>
      </div>
      {canEdit ? (
        <div className="tw:flex tw:gap-2 tw:border-t tw:border-border tw:pt-4">
          {completedAt ? (
            <Button type="button" variant="ghost" onClick={onRestart}>
              Start over
            </Button>
          ) : null}
          <Button
            type="button"
            className="tw:ml-auto"
            disabled={completing}
            onClick={onComplete}
          >
            {completedAt ? 'Done' : 'Finish setup'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
